import type { ReactNode } from 'react';
import { AlertTriangle, Check, Info } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Icon } from '@/components/ui/Icon';
import { cn } from '@/lib/cn';

/**
 * Design system §11. Say what happened, then what to do next.
 * Lead with a bold sentence; put the action in `children`.
 */
export type AlertTone = 'info' | 'success' | 'warning' | 'danger';

const TONE: Record<AlertTone, { className: string; icon: LucideIcon }> = {
  info: { className: 'bg-academic-tint text-academic-strong', icon: Info },
  success: { className: 'bg-success-tint text-success-strong', icon: Check },
  warning: { className: 'bg-warning-tint text-warning-strong', icon: AlertTriangle },
  danger: { className: 'bg-danger-tint text-danger-strong', icon: Info },
};

export interface AlertProps {
  tone?: AlertTone;
  /** Bold opening sentence — what happened. */
  title: string;
  /** What to do next. */
  children?: ReactNode;
  className?: string;
}

export function Alert({ tone = 'info', title, children, className }: AlertProps) {
  const spec = TONE[tone];

  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn('flex gap-3 rounded-[12px] px-4 py-3.5', spec.className, className)}
    >
      <span className="mt-0.5 shrink-0">
        <Icon icon={spec.icon} size={20} />
      </span>
      <div className="text-[15px]">
        <b>{title}</b>
        {children ? <> {children}</> : null}
      </div>
    </div>
  );
}
